/**
 * ReportListDialogs — mounts every dialog the list view can open
 * (Create / Create category / v2 dialogs). Extracted from
 * `ReportListView.tsx` to keep that file under the 500-line cap.
 */

import { CreateReportDialog } from './dialogs/CreateReportDialog';
import { CreateCategoryDialog } from './dialogs/CreateCategoryDialog';
import { ReportsDialogs } from './ReportsDialogs';
import {
  type AnalyseCategory,
  type AnalyseDashboard,
  type DashboardIndicator,
  type Dataset,
  type ScheduledReportCreatePayload,
} from '../../lib/api/reporting';
import type {
  ExportFormat,
  NewCategoryState,
  NewReportState,
} from './useReports';

export interface ReportListDialogsProps {
  selectedReport: AnalyseDashboard | null;
  indicators: DashboardIndicator[];
  categories: AnalyseCategory[];
  datasets: Dataset[];
  saving: boolean;

  // v1 dialog state
  isCreateReportOpen: boolean;
  setIsCreateReportOpen: (open: boolean) => void;
  isCreateCategoryOpen: boolean;
  setIsCreateCategoryOpen: (open: boolean) => void;
  newReport: NewReportState;
  setNewReport: (n: NewReportState | ((prev: NewReportState) => NewReportState)) => void;
  newCategory: NewCategoryState;
  setNewCategory: (n: NewCategoryState) => void;
  onCreateReport: () => void;
  onCreateCategory: () => void;

  // v2 dialog state
  isEditOpen: boolean;
  setIsEditOpen: (open: boolean) => void;
  reportToEdit: AnalyseDashboard | null;
  isDuplicateOpen: boolean;
  setIsDuplicateOpen: (open: boolean) => void;
  reportToDuplicate: AnalyseDashboard | null;
  isShareOpen: boolean;
  setIsShareOpen: (open: boolean) => void;
  reportToShare: AnalyseDashboard | null;
  isPublishOpen: boolean;
  setIsPublishOpen: (open: boolean) => void;
  reportToPublish: AnalyseDashboard | null;
  openPublishReport: (report: AnalyseDashboard) => void;
  isRevisionsOpen: boolean;
  setIsRevisionsOpen: (open: boolean) => void;
  reportForRevisions: AnalyseDashboard | null;
  openRevisionHistory: (report: AnalyseDashboard) => void;
  isDownloadLogsOpen: boolean;
  setIsDownloadLogsOpen: (open: boolean) => void;
  openDownloadLogs: () => void;
  isFiscalCalendarOpen: boolean;
  setIsFiscalCalendarOpen: (open: boolean) => void;
  openFiscalCalendar: () => void;
  isScheduleOpen: boolean;
  setIsScheduleOpen: (open: boolean) => void;
  isExportOpen: boolean;
  setIsExportOpen: (open: boolean) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  isEditIndicatorOpen: boolean;
  setIsEditIndicatorOpen: (open: boolean) => void;
  indicatorToEdit: DashboardIndicator | null;
  isDeleteReportOpen: boolean;
  setIsDeleteReportOpen: (open: boolean) => void;
  reportToDelete: AnalyseDashboard | null;
  setReportToDelete: (r: AnalyseDashboard | null) => void;

  // v2 actions
  onEditReport: (payload: Partial<AnalyseDashboard>) => void;
  onDuplicateReport: (name: string) => void;
  onShareReport: (sharedWith: AnalyseDashboard['shared_with']) => Promise<void> | void;
  onScheduleReport: (payload: ScheduledReportCreatePayload) => void;
  onExportReport: (format: ExportFormat, applyFilters: boolean, indicatorIds?: number[]) => void;
  onAfterFilterChange: () => void;
  onEditIndicator: (payload: Partial<DashboardIndicator>) => void;
  onOpenIndicatorBuilder: (indicator: DashboardIndicator) => void;
  onDeleteReport: () => void;
}

export function ReportListDialogs(props: ReportListDialogsProps) {
  return (
    <>
      <CreateReportDialog
        open={props.isCreateReportOpen}
        onOpenChange={props.setIsCreateReportOpen}
        newReport={props.newReport}
        onReportChange={props.setNewReport}
        categories={props.categories}
        datasets={props.datasets}
        saving={props.saving}
        onSubmit={props.onCreateReport}
      />

      <CreateCategoryDialog
        open={props.isCreateCategoryOpen}
        onOpenChange={props.setIsCreateCategoryOpen}
        newCategory={props.newCategory}
        onCategoryChange={props.setNewCategory}
        saving={props.saving}
        onSubmit={props.onCreateCategory}
      />

      <ReportsDialogs
        selectedReport={props.selectedReport}
        firstIndicatorId={props.indicators[0]?.id ?? null}
        categories={props.categories}
        datasets={props.datasets}
        saving={props.saving}
        isEditOpen={props.isEditOpen}
        setIsEditOpen={props.setIsEditOpen}
        reportToEdit={props.reportToEdit}
        onEditReport={props.onEditReport}
        isDuplicateOpen={props.isDuplicateOpen}
        setIsDuplicateOpen={props.setIsDuplicateOpen}
        reportToDuplicate={props.reportToDuplicate}
        onDuplicateReport={props.onDuplicateReport}
        isShareOpen={props.isShareOpen}
        setIsShareOpen={props.setIsShareOpen}
        reportToShare={props.reportToShare}
        onShareReport={props.onShareReport}
        isPublishOpen={props.isPublishOpen}
        setIsPublishOpen={props.setIsPublishOpen}
        reportToPublish={props.reportToPublish}
        isRevisionsOpen={props.isRevisionsOpen}
        setIsRevisionsOpen={props.setIsRevisionsOpen}
        reportForRevisions={props.reportForRevisions}
        isDownloadLogsOpen={props.isDownloadLogsOpen}
        setIsDownloadLogsOpen={props.setIsDownloadLogsOpen}
        isFiscalCalendarOpen={props.isFiscalCalendarOpen}
        setIsFiscalCalendarOpen={props.setIsFiscalCalendarOpen}
        isScheduleOpen={props.isScheduleOpen}
        setIsScheduleOpen={props.setIsScheduleOpen}
        onScheduleReport={props.onScheduleReport}
        isExportOpen={props.isExportOpen}
        setIsExportOpen={props.setIsExportOpen}
        onExportReport={props.onExportReport}
        isFilterOpen={props.isFilterOpen}
        setIsFilterOpen={props.setIsFilterOpen}
        onAfterFilterChange={props.onAfterFilterChange}
        isEditIndicatorOpen={props.isEditIndicatorOpen}
        setIsEditIndicatorOpen={props.setIsEditIndicatorOpen}
        indicatorToEdit={props.indicatorToEdit}
        onEditIndicator={props.onEditIndicator}
        onOpenIndicatorBuilder={props.onOpenIndicatorBuilder}
        isDeleteReportOpen={props.isDeleteReportOpen}
        setIsDeleteReportOpen={props.setIsDeleteReportOpen}
        reportToDelete={props.reportToDelete}
        onCancelDelete={() => props.setReportToDelete(null)}
        onConfirmDelete={props.onDeleteReport}
      />
    </>
  );
}
